/**
 * Promotions panel — the superadmin surface for promo grants + codes
 * (promo plan Phase P3), rendered inside the Tier-1 PlatformBudgetAdmin view.
 *
 * A promo lives entirely on the entitlement side (never Stripe): a grant boosts
 * a tenant's included budget / storage / concurrency, unlocks features, or
 * overrides its plan tier, for a mandatory `[startsAt, endsAt)` window. A code
 * mints the same kind of grant on redemption (a `maxRedemptions=1` code IS a
 * one-time link).
 *
 * Copy lives in the `billing` i18n namespace (`promo.*`, all four locales).
 * This supersedes the panel's original deliberately-English-only stance: it
 * renders inside PlatformBudgetAdmin, which is fully localized, so an
 * English-only block made the one screen bilingual for non-en admins.
 * Tenant-facing promo copy (P5) still gets its own keys when it lands.
 *
 * Money is entered in the currency this deployment CHARGES in and stored as
 * billing-currency micro; storage in GB and stored as bytes — the two unit
 * conversions live in `draftToEffect` so nothing else has to remember them.
 *
 * The currency is never named by the client: `PromoEffectInput` omits
 * `billingCurrency` and the SERVER stamps it at mint, so a panel that guessed
 * could mint a grant in a currency the pool is not held in. What the panel owes
 * the operator is the currency's NAME on the box they type into — this said
 * "USD" on every deployment until 2026-08-18, so on a MYR install typing 30 for
 * "US$30" minted RM30, and the rows below printed that RM30 back as "$30.00".
 *
 * Plan: docs/plans/2026-07-22_PROMO_ACCESS_MODULAR_BILLING.md
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ActionDisclosure, useBillingCurrency, useConfirm, useToast,
} from '@papan/core-ui/ui'
import { InfoHint } from './CatalogAdmin'
import { useTranslation } from '@papan/i18n'
import type { PromoEffect, PromoEffectInput, PromoEffectKind } from '@papan/types'
import { PLATFORM_DATE_LOCALE } from '@papan/types'
import { billingApi, type PlanView } from '../client/index.js'
import {
  promoAdminApi,
  type CampaignPreview,
  type PromoGrantView,
  type PromoCodeView,
} from '../client/promo-admin.js'
import { formatBillingMicro } from './money.js'

/** A micro unit is 10^-6 of the MAJOR unit in EVERY currency, so this
 *  conversion is currency-independent — unlike a MINOR unit, which is 100 per
 *  major in MYR, 1 in JPY and 1000 in BHD. It was called `USD_MICRO`, which is
 *  what made "divide by it and prefix a dollar sign" look correct. */
const BILLING_MICRO = 1_000_000
const GB = 1024 ** 3

/** The translate function shape the helpers below need. */
type T = (key: string, opts?: Record<string, unknown>) => string

// ─── Effect drafts ────────────────────────────────────────────────────────
// A draft holds the UI-facing (string/number) fields; `draftToEffect` converts
// it into a validated PromoEffect (USD→micro, GB→bytes, csv→string[]) or throws
// a human message the form surfaces.

interface EffectDraft {
  kind: PromoEffectKind
  planId?: string
  /** Major units of the deployment's billing currency, as typed. */
  amount?: string
  gb?: string
  slots?: string
  features?: string
}

const EFFECT_KINDS: PromoEffectKind[] = [
  'credit', 'storage_boost', 'concurrency_boost', 'plan_override', 'feature_unlock',
]

function draftToEffect(d: EffectDraft, t: T): PromoEffectInput {
  switch (d.kind) {
    case 'plan_override': {
      if (!d.planId) throw new Error(t('promo.err.pickPlan'))
      return { kind: 'plan_override', planId: d.planId }
    }
    case 'credit': {
      const amount = Number(d.amount)
      if (!Number.isFinite(amount) || amount <= 0) throw new Error(t('promo.err.budget'))
      // No `billingCurrency`: it is a fact about the DEPLOYMENT, stamped by the
      // server at mint. A client that named one could mint a grant in a
      // currency the pool is not held in — which the consumer would correctly
      // refuse to spend, producing a grant that exists and can never be drawn.
      return { kind: 'credit', amountBillingMicro: Math.round(amount * BILLING_MICRO) }
    }
    case 'storage_boost': {
      const gb = Number(d.gb)
      if (!Number.isFinite(gb) || gb <= 0) throw new Error(t('promo.err.storage'))
      return { kind: 'storage_boost', bytes: Math.round(gb * GB) }
    }
    case 'concurrency_boost': {
      const slots = Number(d.slots)
      if (!Number.isInteger(slots) || slots <= 0) throw new Error(t('promo.err.concurrency'))
      return { kind: 'concurrency_boost', slots }
    }
    case 'feature_unlock': {
      const features = (d.features ?? '').split(',').map((s) => s.trim()).filter(Boolean)
      if (!features.length) throw new Error(t('promo.err.features'))
      return { kind: 'feature_unlock', features }
    }
  }
}

/** One-line human summary of a stored effect, for grant/code lists. */
function describeEffect(e: PromoEffect, t: T): string {
  switch (e.kind) {
    case 'plan_override':
      return t('promo.effect.plan', { plan: e.planId })
    case 'credit':
      // The GRANT's stamped currency, not the deployment's: a grant minted
      // before a re-denomination must keep reading in the currency it was
      // minted in. `CreditEffectSchema` REQUIRES the field — 0177 refuses to
      // migrate a database with any surviving promo row — so there is no
      // unstamped effect to be lenient for, and a fallback here would be the
      // permissive default that schema comment exists to refuse.
      return t('promo.effect.credit', {
        amount: formatBillingMicro(e.amountBillingMicro, e.billingCurrency),
      })
    case 'storage_boost':
      return t('promo.effect.storage', { gb: (e.bytes / GB).toLocaleString() })
    case 'concurrency_boost':
      return t('promo.effect.concurrency', { slots: e.slots })
    case 'feature_unlock':
      return t('promo.effect.unlock', { features: e.features.join(', ') })
    default:
      return t('promo.effect.unknown')
  }
}

// Platform-shelf panel → Malaysia date order (en-MY); grant windows keep their
// time-of-day and local timezone (an instant, not a UTC civil date).
const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString(PLATFORM_DATE_LOCALE) : '—')

/** A grant is active iff started, not yet ended, and not revoked. */
function grantStatus(g: PromoGrantView, t: T): { label: string; cls: string } {
  const now = Date.now()
  if (g.revokedAt) return { label: t('promo.status.revoked'), cls: 'bg-red-500/20 text-red-300' }
  if (new Date(g.endsAt).getTime() <= now) return { label: t('promo.status.expired'), cls: 'themed-bg-tertiary themed-text-muted' }
  if (new Date(g.startsAt).getTime() > now) return { label: t('promo.status.scheduled'), cls: 'bg-amber-500/20 text-amber-300' }
  return { label: t('promo.status.active'), cls: 'bg-emerald-500/20 text-emerald-300' }
}

// ─── Effect builder ─────────────────────────────────────────────────────────

function EffectBuilder({
  drafts,
  onChange,
  plans,
  disabled,
}: {
  drafts: EffectDraft[]
  onChange: (next: EffectDraft[]) => void
  plans: PlanView[]
  disabled?: boolean
}) {
  const { t } = useTranslation('billing')
  // What this deployment CHARGES in. Only the LABEL needs it — the amount is
  // sent unstamped and the server names the currency at mint (see the module
  // docblock), so this can never decide what a grant is denominated in.
  const currency = useBillingCurrency().toUpperCase()
  const update = (i: number, patch: Partial<EffectDraft>) =>
    onChange(drafts.map((d, j) => (j === i ? { ...d, ...patch } : d)))
  const remove = (i: number) => onChange(drafts.filter((_, j) => j !== i))

  return (
    <div className="space-y-2">
      {drafts.map((d, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2 themed-bg-tertiary rounded px-2 py-1.5">
          <select
            className="themed-input text-xs rounded px-2 py-1"
            value={d.kind}
            disabled={disabled}
            aria-label={t('promo.form.effects')}
            onChange={(e) => update(i, { kind: e.target.value as PromoEffectKind })}
          >
            {EFFECT_KINDS.map((kind) => (
              <option key={kind} value={kind}>{t(`promo.kind.${kind}`, { currency })}</option>
            ))}
          </select>

          {d.kind === 'plan_override' && (
            <select
              className="themed-input text-xs rounded px-2 py-1"
              value={d.planId ?? ''}
              disabled={disabled}
              aria-label={t('promo.kind.plan_override')}
              onChange={(e) => update(i, { planId: e.target.value })}
            >
              <option value="">{t('promo.form.selectPlan')}</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
              ))}
            </select>
          )}
          {d.kind === 'credit' && (
            <input
              type="number" min="0" step="0.01" placeholder={currency}
              aria-label={t('promo.kind.credit', { currency })}
              className="themed-input text-xs rounded px-2 py-1 w-28"
              value={d.amount ?? ''} disabled={disabled}
              onChange={(e) => update(i, { amount: e.target.value })}
            />
          )}
          {d.kind === 'storage_boost' && (
            <input
              type="number" min="0" step="0.1" placeholder={t('promo.form.gbPh')}
              aria-label={t('promo.kind.storage_boost')}
              className="themed-input text-xs rounded px-2 py-1 w-28"
              value={d.gb ?? ''} disabled={disabled}
              onChange={(e) => update(i, { gb: e.target.value })}
            />
          )}
          {d.kind === 'concurrency_boost' && (
            <input
              type="number" min="0" step="1" placeholder={t('promo.form.slotsPh')}
              aria-label={t('promo.kind.concurrency_boost')}
              className="themed-input text-xs rounded px-2 py-1 w-24"
              value={d.slots ?? ''} disabled={disabled}
              onChange={(e) => update(i, { slots: e.target.value })}
            />
          )}
          {d.kind === 'feature_unlock' && (
            <input
              type="text" placeholder={t('promo.form.featuresPh')}
              aria-label={t('promo.kind.feature_unlock')}
              className="themed-input text-xs rounded px-2 py-1 flex-1 min-w-[180px]"
              value={d.features ?? ''} disabled={disabled}
              onChange={(e) => update(i, { features: e.target.value })}
            />
          )}

          <button
            type="button" disabled={disabled}
            className="ml-auto text-xs px-2 py-1 rounded themed-btn-secondary disabled:opacity-40"
            onClick={() => remove(i)}
          >
            {t('promo.form.remove')}
          </button>
        </div>
      ))}
      <button
        type="button" disabled={disabled}
        className="text-xs px-2 py-1 rounded themed-btn-secondary disabled:opacity-40"
        onClick={() => onChange([...drafts, { kind: 'credit' }])}
      >
        {t('promo.form.addEffect')}
      </button>
    </div>
  )
}

function buildEffects(drafts: EffectDraft[], t: T): PromoEffectInput[] {
  if (!drafts.length) throw new Error(t('promo.err.noEffects'))
  return drafts.map((d) => draftToEffect(d, t))
}

// ─── Grant form ───────────────────────────────────────────────────────────

/**
 * `fixedTenant` is what lets this form live on a customer's own page.
 *
 * Rendered from the customers LIST it needs a tenant-ID box, which is the thing
 * that made "grant a promo" a paste-the-id chore on a screen whose whole premise
 * is that you click a customer. Given the tenant, the field becomes a stated
 * fact instead of an input — one fewer thing to get wrong, and no way to grant
 * credit to the wrong org by mistyping an id that is valid for another one.
 */
function GrantForm({ plans, busy, onCreated, fixedTenant }: {
  plans: PlanView[]
  busy: boolean
  onCreated: () => void
  fixedTenant?: { id: string; name: string }
}) {
  const { t } = useTranslation('billing')
  const toast = useToast()
  const [typedTenantId, setTypedTenantId] = useState('')
  const tenantId = fixedTenant?.id ?? typedTenantId
  const setTenantId = setTypedTenantId
  const [drafts, setDrafts] = useState<EffectDraft[]>([{ kind: 'credit' }])
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [note, setNote] = useState('')
  const [groupTag, setGroupTag] = useState('')
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    let effects: PromoEffectInput[]
    try {
      effects = buildEffects(drafts, t)
    } catch (e: any) {
      toast.error(e?.message || t('promo.err.invalidEffects'))
      return
    }
    if (!tenantId.trim()) return toast.error(t('promo.err.tenantRequired'))
    if (!startsAt || !endsAt) return toast.error(t('promo.err.datesRequired'))
    setSaving(true)
    try {
      await promoAdminApi.createGrant({
        tenantId: tenantId.trim(),
        effects,
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        note: note.trim() || undefined,
        groupTag: groupTag.trim() || undefined,
      })
      toast.success(t('promo.toast.grantCreated'))
      setDrafts([{ kind: 'credit' }])
      setNote('')
      onCreated()
    } catch (e: any) {
      toast.error(e?.message || t('promo.toast.grantFailed'))
    } finally {
      setSaving(false)
    }
  }

  const disabled = busy || saving
  return (
    <div className="themed-bg-secondary rounded-lg p-3 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {/* The picker renders ONLY when the tenant is not already fixed (F-31).
            On a customer's own page it was a read-only field repeating the name
            and id from the persistent header two inches above — a labelled input
            that could not be typed into, restating what the page is already
            about. Where the form is NOT fixed to a tenant (the Operations
            screen) the picker is the whole point and stays. */}
        {!fixedTenant && (
          <label className="block">
            <span className="text-xs themed-text-secondary">{t('promo.form.tenantId')}</span>
            <input
              type="text" placeholder={t('promo.form.tenantIdPh')}
              className="themed-input text-sm rounded px-2 py-1 w-full mt-0.5"
              value={tenantId} disabled={disabled}
              onChange={(e) => setTenantId(e.target.value)}
            />
          </label>
        )}
        <label className="block">
          <span className="text-xs themed-text-secondary">{t('promo.form.note')}</span>
          <input
            type="text" placeholder={t('promo.form.notePh')}
            className="themed-input text-sm rounded px-2 py-1 w-full mt-0.5"
            value={note} disabled={disabled}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-xs themed-text-secondary">{t('promo.form.starts')}</span>
          <input
            type="datetime-local"
            className="themed-input text-sm rounded px-2 py-1 w-full mt-0.5"
            value={startsAt} disabled={disabled}
            onChange={(e) => setStartsAt(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-xs themed-text-secondary">{t('promo.form.ends')}</span>
          <input
            type="datetime-local"
            className="themed-input text-sm rounded px-2 py-1 w-full mt-0.5"
            value={endsAt} disabled={disabled}
            onChange={(e) => setEndsAt(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-xs themed-text-secondary">{t('promo.form.groupTag')}</span>
          <input
            type="text" placeholder={t('promo.form.groupTagPh')}
            className="themed-input text-sm rounded px-2 py-1 w-full mt-0.5 font-mono"
            value={groupTag} disabled={disabled}
            onChange={(e) => setGroupTag(e.target.value)}
          />
        </label>
      </div>
      <div>
        <span className="text-xs themed-text-secondary">{t('promo.form.effects')}</span>
        <div className="mt-1">
          <EffectBuilder drafts={drafts} onChange={setDrafts} plans={plans} disabled={disabled} />
        </div>
      </div>
      <button
        type="button" disabled={disabled}
        className="px-3 py-1.5 text-sm rounded themed-btn-primary disabled:opacity-40"
        onClick={submit}
      >
        {saving ? t('promo.form.creating') : t('promo.form.createGrant')}
      </button>
    </div>
  )
}

// ─── Grant list (per tenant) ────────────────────────────────────────────────

function GrantList({ tenantId, refreshKey, canEdit, onRevoked }: { tenantId: string; refreshKey: number; canEdit: boolean; onRevoked: () => void }) {
  const { t } = useTranslation('billing')
  const toast = useToast()
  const [grants, setGrants] = useState<PromoGrantView[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  // Revoking a grant claws back credit — a write. The LIST itself is a read and
  // stays visible; only the per-row action is gated.
  const busy = saving || !canEdit

  const load = useCallback(async () => {
    if (!tenantId.trim()) { setGrants(null); return }
    try {
      const res = await promoAdminApi.listGrants({ tenantId: tenantId.trim() })
      setGrants(res.grants)
      setError(null)
    } catch (e: any) {
      setError(e?.message || t('promo.list.loadGrantsFailed'))
      setGrants(null)
    }
  }, [tenantId, t])

  useEffect(() => { void load() }, [load, refreshKey])

  const revoke = async (id: string) => {
    setSaving(true)
    try {
      await promoAdminApi.revokeGrant(id)
      toast.success(t('promo.toast.grantRevoked'))
      await load()
      onRevoked()
    } catch (e: any) {
      toast.error(e?.message || t('promo.toast.revokeFailed'))
    } finally {
      setSaving(false)
    }
  }

  if (!tenantId.trim()) return <p className="themed-text-muted text-xs">{t('promo.list.enterTenant')}</p>
  if (error) return <p className="text-xs text-red-400">{error}</p>
  if (!grants) return <p className="themed-text-muted text-xs">{t('promo.list.loading')}</p>
  if (!grants.length) return <p className="themed-text-muted text-xs">{t('promo.list.noGrants')}</p>

  return (
    <div className="themed-bg-secondary rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b themed-border">
            <th className="text-left px-3 py-2 themed-text-secondary font-medium">{t('promo.col.status')}</th>
            <th className="text-left px-3 py-2 themed-text-secondary font-medium">{t('promo.col.effects')}</th>
            <th className="text-left px-3 py-2 themed-text-secondary font-medium">{t('promo.col.window')}</th>
            <th className="text-left px-3 py-2 themed-text-secondary font-medium">{t('promo.col.source')}</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {grants.map((g) => {
            const st = grantStatus(g, t)
            const canRevoke = !g.revokedAt && new Date(g.endsAt).getTime() > Date.now()
            return (
              <tr key={g.id} className="border-b themed-border last:border-b-0 align-top">
                <td className="px-3 py-2.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${st.cls}`}>{st.label}</span>
                </td>
                <td className="px-3 py-2.5 themed-text">
                  {g.effects.map((e, i) => <div key={i}>{describeEffect(e, t)}</div>)}
                  {(() => {
                    const credits = g.effects.filter((e) => e.kind === 'credit')
                    const granted = credits.reduce((s, e) => s + e.amountBillingMicro, 0)
                    if (granted <= 0) return null
                    const left = Math.max(0, granted - (g.creditConsumedBillingMicro ?? 0))
                    // One grant's credit effects are minted together and so
                    // share a currency in practice. If they ever did not, this
                    // total would be a sum across currencies — which the house
                    // rule refuses to LABEL rather than picking one symbol for
                    // (`singleDisplayCurrencyOver`, `PromoCampaignGrantCount
                    // .billingCurrency`). Unlabelled major units are ugly and
                    // honest; a symbol would be a confident wrong answer.
                    const currencies = [...new Set(credits.map((e) => e.billingCurrency))]
                    const amount = currencies.length === 1
                      ? formatBillingMicro(left, currencies[0]!)
                      : (left / BILLING_MICRO).toLocaleString()
                    return (
                      <div className="text-[10px] themed-text-muted mt-0.5 tabular-nums">
                        {t('promo.list.creditLeft', { amount })}
                      </div>
                    )
                  })()}
                  {g.groupTag && (
                    <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded themed-bg-tertiary themed-text-secondary font-mono">{g.groupTag}</span>
                  )}
                  {g.note && <div className="text-[10px] themed-text-muted mt-0.5">{g.note}</div>}
                </td>
                <td className="px-3 py-2.5 themed-text-secondary text-xs">
                  {fmtDate(g.startsAt)}<br />→ {fmtDate(g.endsAt)}
                </td>
                <td className="px-3 py-2.5 themed-text-secondary text-xs">
                  {g.source}{g.grantedByEmail ? <div className="text-[10px] themed-text-muted">{g.grantedByEmail}</div> : null}
                </td>
                <td className="px-3 py-2.5 text-right">
                  {canRevoke && (
                    <button
                      type="button" disabled={busy}
                      className="text-xs px-2 py-1 rounded themed-btn-secondary disabled:opacity-40"
                      onClick={() => revoke(g.id)}
                    >
                      {t('promo.list.revoke')}
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─── Code form + list ───────────────────────────────────────────────────────

function CodeForm({ plans, onCreated }: { plans: PlanView[]; onCreated: () => void }) {
  const { t } = useTranslation('billing')
  const toast = useToast()
  const [drafts, setDrafts] = useState<EffectDraft[]>([{ kind: 'credit' }])
  const [label, setLabel] = useState('')
  const [vanity, setVanity] = useState('')
  const [groupTag, setGroupTag] = useState('')
  const [windowMode, setWindowMode] = useState<'duration' | 'fixed'>('duration')
  const [durationDays, setDurationDays] = useState('30')
  const [grantStartsAt, setGrantStartsAt] = useState('')
  const [grantEndsAt, setGrantEndsAt] = useState('')
  const [oneTime, setOneTime] = useState(true)
  const [maxRedemptions, setMaxRedemptions] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    let effects: PromoEffectInput[]
    try {
      effects = buildEffects(drafts, t)
    } catch (e: any) {
      toast.error(e?.message || t('promo.err.invalidEffects'))
      return
    }
    const input: Parameters<typeof promoAdminApi.createCode>[0] = { effects }
    if (label.trim()) input.label = label.trim()
    if (vanity.trim()) input.code = vanity.trim()
    if (groupTag.trim()) input.groupTag = groupTag.trim()
    if (windowMode === 'duration') {
      const d = Number(durationDays)
      if (!Number.isInteger(d) || d <= 0) return toast.error(t('promo.err.duration'))
      input.durationDays = d
    } else {
      if (!grantStartsAt || !grantEndsAt) return toast.error(t('promo.err.fixedWindow'))
      input.grantStartsAt = new Date(grantStartsAt).toISOString()
      input.grantEndsAt = new Date(grantEndsAt).toISOString()
    }
    if (oneTime) {
      input.maxRedemptions = 1
    } else if (maxRedemptions.trim()) {
      const m = Number(maxRedemptions)
      if (!Number.isInteger(m) || m <= 0) return toast.error(t('promo.err.maxRedemptions'))
      input.maxRedemptions = m
    } // else leave unset → unlimited
    if (expiresAt) input.expiresAt = new Date(expiresAt).toISOString()

    setSaving(true)
    try {
      await promoAdminApi.createCode(input)
      toast.success(t('promo.toast.codeCreated'))
      setDrafts([{ kind: 'credit' }])
      setLabel('')
      setVanity('')
      onCreated()
    } catch (e: any) {
      toast.error(e?.message || t('promo.toast.codeFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="themed-bg-secondary rounded-lg p-3 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block">
          <span className="text-xs themed-text-secondary">{t('promo.form.label')}</span>
          <input
            type="text" placeholder={t('promo.form.labelPh')}
            className="themed-input text-sm rounded px-2 py-1 w-full mt-0.5"
            value={label} disabled={saving}
            onChange={(e) => setLabel(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-xs themed-text-secondary">{t('promo.form.vanity')}</span>
          <input
            type="text" placeholder={t('promo.form.vanityPh')}
            className="themed-input text-sm rounded px-2 py-1 w-full mt-0.5 font-mono"
            value={vanity} disabled={saving}
            onChange={(e) => setVanity(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-xs themed-text-secondary">{t('promo.form.groupTag')}</span>
          <input
            type="text" placeholder={t('promo.form.groupTagPh')}
            className="themed-input text-sm rounded px-2 py-1 w-full mt-0.5 font-mono"
            value={groupTag} disabled={saving}
            onChange={(e) => setGroupTag(e.target.value)}
          />
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs themed-text-secondary">{t('promo.form.window')}</span>
        <label className="flex items-center gap-1 text-xs themed-text">
          <input type="radio" checked={windowMode === 'duration'} disabled={saving} onChange={() => setWindowMode('duration')} />
          {t('promo.form.rolling')}
        </label>
        {windowMode === 'duration' && (
          <input
            type="number" min="1" step="1"
            aria-label={t('promo.form.daysFromRedemption')}
            className="themed-input text-xs rounded px-2 py-1 w-20"
            value={durationDays} disabled={saving}
            onChange={(e) => setDurationDays(e.target.value)}
          />
        )}
        {windowMode === 'duration' && <span className="text-xs themed-text-muted">{t('promo.form.daysFromRedemption')}</span>}
        <label className="flex items-center gap-1 text-xs themed-text">
          <input type="radio" checked={windowMode === 'fixed'} disabled={saving} onChange={() => setWindowMode('fixed')} />
          {t('promo.form.fixedDates')}
        </label>
        {windowMode === 'fixed' && (
          <>
            <input type="datetime-local" className="themed-input text-xs rounded px-2 py-1" value={grantStartsAt} disabled={saving} onChange={(e) => setGrantStartsAt(e.target.value)} />
            <span className="text-xs themed-text-muted">→</span>
            <input type="datetime-local" className="themed-input text-xs rounded px-2 py-1" value={grantEndsAt} disabled={saving} onChange={(e) => setGrantEndsAt(e.target.value)} />
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-1 text-xs themed-text">
          <input type="checkbox" checked={oneTime} disabled={saving} onChange={(e) => setOneTime(e.target.checked)} />
          {t('promo.form.oneTime')}
        </label>
        {!oneTime && (
          <label className="flex items-center gap-1 text-xs themed-text">
            {t('promo.form.maxRedemptions')}
            <input
              type="number" min="1" step="1" placeholder="∞"
              className="themed-input text-xs rounded px-2 py-1 w-20"
              value={maxRedemptions} disabled={saving}
              onChange={(e) => setMaxRedemptions(e.target.value)}
            />
            <span className="themed-text-muted">{t('promo.form.blankUnlimited')}</span>
          </label>
        )}
        <label className="flex items-center gap-1 text-xs themed-text">
          {t('promo.form.redeemableUntil')}
          <input type="datetime-local" className="themed-input text-xs rounded px-2 py-1" value={expiresAt} disabled={saving} onChange={(e) => setExpiresAt(e.target.value)} />
        </label>
      </div>

      <div>
        <span className="text-xs themed-text-secondary">{t('promo.form.effects')}</span>
        <div className="mt-1">
          <EffectBuilder drafts={drafts} onChange={setDrafts} plans={plans} disabled={saving} />
        </div>
      </div>

      <button
        type="button" disabled={saving}
        className="px-3 py-1.5 text-sm rounded themed-btn-primary disabled:opacity-40"
        onClick={submit}
      >
        {saving ? t('promo.form.creating') : t('promo.form.createCode')}
      </button>
    </div>
  )
}

function CodeList({ refreshKey, canEdit, onChanged }: { refreshKey: number; canEdit: boolean; onChanged: () => void }) {
  const { t } = useTranslation('billing')
  const toast = useToast()
  const [codes, setCodes] = useState<PromoCodeView[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const busy = saving || !canEdit

  const load = useCallback(async () => {
    try {
      setCodes((await promoAdminApi.listCodes()).codes)
      setError(null)
    } catch (e: any) {
      setError(e?.message || t('promo.list.loadCodesFailed'))
    }
  }, [t])

  useEffect(() => { void load() }, [load, refreshKey])

  const revoke = async (id: string) => {
    setSaving(true)
    try {
      await promoAdminApi.revokeCode(id)
      toast.success(t('promo.toast.codeRevoked'))
      await load()
      onChanged()
    } catch (e: any) {
      toast.error(e?.message || t('promo.toast.revokeFailed'))
    } finally {
      setSaving(false)
    }
  }

  const copyLink = async (code: string) => {
    const link = `${window.location.origin}/promo/${code}`
    try {
      await navigator.clipboard.writeText(link)
      toast.success(t('promo.toast.linkCopied'))
    } catch {
      toast.error(link)
    }
  }

  if (error) return <p className="text-xs text-red-400">{error}</p>
  if (!codes) return <p className="themed-text-muted text-xs">{t('promo.list.loading')}</p>
  if (!codes.length) return <p className="themed-text-muted text-xs">{t('promo.list.noCodes')}</p>

  return (
    <div className="themed-bg-secondary rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b themed-border">
            <th className="text-left px-3 py-2 themed-text-secondary font-medium">{t('promo.col.code')}</th>
            <th className="text-left px-3 py-2 themed-text-secondary font-medium">{t('promo.col.effects')}</th>
            <th className="text-left px-3 py-2 themed-text-secondary font-medium">{t('promo.col.window')}</th>
            <th className="text-left px-3 py-2 themed-text-secondary font-medium">{t('promo.col.redemptions')}</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {codes.map((code) => {
            const revoked = !!code.revokedAt
            const window = code.durationDays != null
              ? t('promo.list.daysWindow', { days: code.durationDays })
              : `${fmtDate(code.grantStartsAt)} → ${fmtDate(code.grantEndsAt)}`
            const cap = code.maxRedemptions == null ? '∞' : code.maxRedemptions
            return (
              <tr key={code.id} className={`border-b themed-border last:border-b-0 align-top ${revoked ? 'opacity-50' : ''}`}>
                <td className="px-3 py-2.5">
                  <div className="font-mono themed-text">{code.code}</div>
                  {code.label && <div className="text-[10px] themed-text-muted">{code.label}</div>}
                  {code.groupTag && (
                    <span className="inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded themed-bg-tertiary themed-text-secondary font-mono">{code.groupTag}</span>
                  )}
                  {revoked && <div className="text-[10px] text-red-300">{t('promo.list.revokedTag')}</div>}
                </td>
                <td className="px-3 py-2.5 themed-text">
                  {code.effects.map((e, i) => <div key={i}>{describeEffect(e, t)}</div>)}
                </td>
                <td className="px-3 py-2.5 themed-text-secondary text-xs">
                  {window}
                  {code.expiresAt && <div className="text-[10px] themed-text-muted">{t('promo.list.redeemBy', { date: fmtDate(code.expiresAt) })}</div>}
                </td>
                <td className="px-3 py-2.5 themed-text">
                  {code.redemptionCount} / {cap}
                  <div className="text-[10px] themed-text-muted">{t('promo.list.perTenant', { limit: code.perTenantLimit })}</div>
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  <button
                    type="button"
                    className="text-xs px-2 py-1 rounded themed-btn-secondary mr-1"
                    onClick={() => copyLink(code.code)}
                  >
                    {t('promo.list.copyLink')}
                  </button>
                  {!revoked && (
                    <button
                      type="button" disabled={busy}
                      className="text-xs px-2 py-1 rounded themed-btn-secondary disabled:opacity-40"
                      onClick={() => revoke(code.id)}
                    >
                      {t('promo.list.revoke')}
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}


// ─── Campaign actions (C1) ──────────────────────────────────────────────────

/**
 * Campaign revoke — CHECK, then act (F-29).
 *
 * ── What was wrong ──────────────────────────────────────────────────────
 *
 * Two red destructive buttons sat permanently open beside a free-text tag box,
 * with no way to find out what a tag matched before firing one. That box is the
 * whole target: a mistyped tag matches nothing (the harmless miss), but a tag
 * that is REUSED across two campaigns, or that an operator half-remembers,
 * matches more than they meant — across tenants, with no un-revoke.
 *
 * ── The shape ───────────────────────────────────────────────────────────
 *
 * The buttons are DISABLED until the typed tag has been checked, and any edit
 * to the box clears the check. So the sentence an operator reads immediately
 * above the button they are about to press ("4 grants across 3 tenants,
 * $210.00 of unspent credit") is always about the tag actually in the box —
 * it cannot go stale behind a subsequent keystroke.
 *
 * The confirm dialog stays. The preview answers "what does this match?"; the
 * dialog answers "are you sure?", and a preview is not a confirmation — it is
 * read before the decision, not as part of making it.
 *
 * A tag matching NOTHING is reported as such and leaves the buttons disabled:
 * there is nothing to revoke, and letting the operator fire anyway would
 * produce a success toast reading "0 grants revoked", which looks like it
 * worked.
 */
function CampaignActions({ onChanged }: { onChanged: () => void }) {
  const { t } = useTranslation('billing')
  const toast = useToast()
  const confirm = useConfirm()
  const [tag, setTag] = useState('')
  const [busy, setBusy] = useState(false)
  const [checking, setChecking] = useState(false)
  /** The preview, and the tag it is FOR. Holding the tag beside it is what
   *  makes "cleared on edit" a comparison rather than a side effect somebody
   *  can forget to fire. */
  const [preview, setPreview] = useState<CampaignPreview | null>(null)

  const checkedTag = preview?.groupTag ?? null
  const current = tag.trim()
  const checked = current !== '' && checkedTag === current
  const grantsCount = preview?.grants.grants ?? 0
  const codesCount = preview?.codes.codes ?? 0

  const check = async () => {
    if (!current) return toast.error(t('promo.campaign.tagRequired'))
    setChecking(true)
    try {
      setPreview(await promoAdminApi.previewCampaign(current))
    } catch (e: any) {
      setPreview(null)
      toast.error(e?.message || t('promo.campaign.checkFailed', { defaultValue: 'Could not check that tag.' }))
    } finally {
      setChecking(false)
    }
  }

  const run = async (kind: 'grants' | 'codes') => {
    const groupTag = tag.trim()
    if (!groupTag) return toast.error(t('promo.campaign.tagRequired'))
    const ok = await confirm({
      title: t('promo.campaign.confirmTitle', { tag: groupTag }),
      message: kind === 'grants' ? t('promo.campaign.confirmGrants') : t('promo.campaign.confirmCodes'),
      confirmText: t('promo.campaign.confirmAction'),
      type: 'danger',
    })
    if (!ok) return
    setBusy(true)
    try {
      if (kind === 'grants') {
        const res = await promoAdminApi.revokeGrantsByTag(groupTag)
        toast.success(t('promo.campaign.grantsRevoked', { grants: res.revokedGrants, tenants: res.affectedTenants }))
      } else {
        const res = await promoAdminApi.revokeCodesByTag(groupTag)
        toast.success(t('promo.campaign.codesRevoked', { codes: res.revokedCodes }))
      }
      // The set just changed, so the check no longer describes reality.
      setPreview(null)
      onChanged()
    } catch (e: any) {
      toast.error(e?.message || t('promo.toast.revokeFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <ActionDisclosure
      tone="danger"
      label={t('promo.campaign.open', { defaultValue: 'End a campaign' })}
      openLabel={t('common:cancel', { defaultValue: 'Cancel' })}
      hint={t('promo.campaign.desc')}
    >
    <div className="themed-bg-secondary rounded-lg p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text" placeholder={t('promo.form.groupTagPh')}
          // NOT `promo.form.groupTag` — that key reads "Campaign tag
          // (optional)", which it is on the grant form and is not here: this
          // box is the entire target of two irreversible bulk revokes.
          aria-label={t('promo.campaign.tagLabel', { defaultValue: 'Campaign tag to end' })}
          className="themed-input text-sm rounded px-2 py-1 w-56 font-mono"
          value={tag} disabled={busy || checking}
          // Any edit invalidates the check — see the header note.
          onChange={(e) => { setTag(e.target.value); setPreview(null) }}
        />
        <button
          type="button" disabled={busy || checking || !current}
          className="text-xs px-2.5 py-1.5 rounded themed-btn-secondary transition-colors disabled:opacity-40"
          onClick={() => void check()}
        >
          {checking
            ? t('promo.campaign.checking', { defaultValue: 'Checking…' })
            : t('promo.campaign.check', { defaultValue: 'Check what this matches' })}
        </button>
      </div>

      {/* `aria-live` because the result of pressing Check is text appearing
          below the button, and it is what arms the two red controls. */}
      <div aria-live="polite">
        {checked && (
          grantsCount === 0 && codesCount === 0 ? (
            <p className="themed-text-muted text-xs">
              {t('promo.campaign.noMatch', {
                defaultValue: 'Nothing matches “{{tag}}”. Check the spelling — tags are exact.',
                tag: current,
              })}
            </p>
          ) : (
            <ul className="text-xs themed-text-secondary space-y-0.5">
              <li>
                {t('promo.campaign.matchGrants', {
                  defaultValue:
                    '{{grants}} live grant across {{tenants}} tenant, holding {{amount}} of credit nobody has spent yet.',
                  defaultValue_other:
                    '{{grants}} live grants across {{tenants}} tenants, holding {{amount}} of credit nobody has spent yet.',
                  count: grantsCount,
                  grants: grantsCount,
                  tenants: preview!.grants.tenants,
                  // Billing-currency micro in the deployment's own currency,
                  // which the preview now carries. It went through
                  // `formatUsdMicro` before, printing an MYR total as "$".
                  // OPTIONAL on the wire, like every other field this branch
                  // added. The SPA (CF Pages) and the API (Hetzner) deploy
                  // separately, so a newer SPA against an older API is a real
                  // window — and `formatBillingMicro` calls
                  // `currency.toUpperCase()`, which THROWS on undefined and
                  // would blank the whole revoke preview rather than one figure.
                  // Falling back to the raw micro amount is ugly and honest; a
                  // hardcoded currency here would be the original bug again.
                  amount: preview!.billingCurrency
                    ? formatBillingMicro(
                      preview!.grants.unspentCreditBillingMicro,
                      preview!.billingCurrency,
                    )
                    : String(preview!.grants.unspentCreditBillingMicro / 1_000_000),
                })}
              </li>
              <li>
                {/* NOT "still redeemable". The codes-side revoke sweeps every
                    un-revoked code carrying the tag, expired ones included —
                    an expired code cannot be redeemed, so that wording would
                    over-claim on exactly the campaigns being cleaned up. Say
                    what the button will do to them instead, which is also the
                    number the preview is pinned to. */}
                {t('promo.campaign.matchCodes', {
                  defaultValue: '{{codes}} code carrying the tag has not been revoked yet.',
                  defaultValue_other: '{{codes}} codes carrying the tag have not been revoked yet.',
                  count: codesCount,
                  codes: codesCount,
                })}
              </li>
              {/* Only when there ARE redemptions, and pluralised on the
                  REDEMPTION count — folding it into the sentence above made
                  the plural follow the number of CODES, which rendered
                  "1 code … redeemed 0 time so far". The sentence also exists
                  to say what revoking codes does NOT do, which is the half of
                  this pair operators get wrong. */}
              {preview!.codes.redemptions > 0 && (
                <li>
                  {t('promo.campaign.matchRedemptions', {
                    defaultValue:
                      'Already redeemed {{count}} time. Revoking the codes stops future redemptions and leaves the grants that were already minted alone — those are the ones above.',
                    defaultValue_other:
                      'Already redeemed {{count}} times. Revoking the codes stops future redemptions and leaves the grants that were already minted alone — those are the ones above.',
                    count: preview!.codes.redemptions,
                  })}
                </li>
              )}
            </ul>
          )
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button" disabled={busy || !checked || grantsCount === 0}
          className="text-xs px-2.5 py-1.5 rounded ring-1 ring-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40"
          onClick={() => void run('grants')}
        >
          {t('promo.campaign.revokeGrants')}
        </button>
        <button
          type="button" disabled={busy || !checked || codesCount === 0}
          className="text-xs px-2.5 py-1.5 rounded ring-1 ring-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40"
          onClick={() => void run('codes')}
        >
          {t('promo.campaign.revokeCodes')}
        </button>
        {!checked && (
          <span className="themed-text-muted text-[11px]">
            {t('promo.campaign.checkFirst', { defaultValue: 'Check the tag first.' })}
          </span>
        )}
      </div>
    </div>
    </ActionDisclosure>
  )
}

// ─── Panel ───────────────────────────────────────────────────────────────────

/**
 * Redeemable promo CODES and campaign revocation — the half of promotions that
 * belongs to nobody in particular.
 *
 * Per-tenant grants moved to `TenantPromotionsPanel`, on the customer's own
 * page. See that component for why.
 */
export function PromotionsPanel({ canEdit = true }: {
  /** Phase 0 (D2): `customers:write`. A code hands out credit, so the whole
   *  authoring surface needs the write; the LISTS stay readable. */
  canEdit?: boolean
} = {}) {
  const { t } = useTranslation('billing')
  const [plans, setPlans] = useState<PlanView[]>([])
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [codeRefresh, setCodeRefresh] = useState(0)

  useEffect(() => {
    billingApi.listPlans()
      .then((r) => { setPlans(r.plans); setEnabled(r.billingEnabled) })
      .catch(() => setEnabled(false))
  }, [])

  // The promo surface only exists when billing is enabled (routes 404 otherwise).
  const bannerOff = useMemo(() => enabled === false, [enabled])

  return (
    <div className="mt-8">
      <h2 className="themed-text text-lg font-medium mb-1">{t('promo.title')}</h2>
      <p className="themed-text-muted text-sm mb-4">{t('promo.subtitle')}</p>

      {bannerOff && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 mb-4">
          <p className="text-amber-300 text-xs font-medium">{t('promo.billingOff')}</p>
          <p className="themed-text-secondary text-xs mt-0.5">{t('promo.billingOffBody')}</p>
        </div>
      )}

      {!canEdit && (
        <div className="rounded-lg border border-blue-500/40 bg-blue-500/10 px-3 py-2 mb-4">
          <p className="text-blue-300 text-xs font-medium">{t('promo.readOnly.title', { defaultValue: 'Read-only' })}</p>
          <p className="themed-text-secondary text-xs mt-0.5">
            {t('promo.readOnly.body', { defaultValue: 'Your role in the platform organization lets you review promotions, but not create or revoke them.' })}
          </p>
        </div>
      )}

      <div className="space-y-8">
        {/* The AUTHORING sections are hidden without the write, not disabled:
            unlike a cap input — where the greyed current value is itself the
            information — an empty create form conveys nothing when inert, and
            three dead forms would dominate a page whose remaining value is the
            lists. The lists stay, because reviewing who holds what credit is
            exactly what a read-only seat is for. */}
        {/* The LIST first now, and the two authoring forms folded beneath it
            (F-28). Operations is a triage screen: the question on arrival is
            "what is out there", and that answer used to start below a
            nine-field create form. */}
        <section>
          <h3 className="themed-text text-sm font-medium mb-2">{t('promo.sections.codes')}</h3>
          <CodeList refreshKey={codeRefresh} canEdit={canEdit} onChanged={() => setCodeRefresh((n) => n + 1)} />
        </section>

        {canEdit && (
        <section>
          <h3 className="themed-text text-sm font-medium mb-2">{t('promo.sections.codeForm')}</h3>
          <ActionDisclosure
            label={t('promo.form.openCode', { defaultValue: 'New promo code' })}
            openLabel={t('common:cancel', { defaultValue: 'Cancel' })}
          >
            <CodeForm plans={plans} onCreated={() => setCodeRefresh((n) => n + 1)} />
          </ActionDisclosure>
        </section>
        )}

        {canEdit && (
        <section>
          <h3 className="themed-text text-sm font-medium mb-2">{t('promo.sections.campaign')}</h3>
          <CampaignActions onChanged={() => setCodeRefresh((n) => n + 1)} />
        </section>
        )}
      </div>
    </div>
  )
}

/**
 * One customer's promotions, for that customer's own page.
 *
 * ── Why this is a second component and not a prop ───────────────────────
 *
 * A promo GRANT is a fact about one tenant; a promo CODE is an offer anybody
 * may redeem. They were one panel because they share a vocabulary, and that put
 * "give this customer credit" behind a tenant-ID paste box on the customers
 * LIST — under a screen whose entire premise is that you click a customer to
 * act on them. The list is the wrong place for either half: one belongs to a
 * customer, the other to nobody in particular.
 *
 * So the per-tenant half moved here and the cross-customer half stayed. Both
 * still render `GrantForm` / `GrantList`, which is what keeps the two from
 * drifting; only the tenant is supplied differently.
 */
export function TenantPromotionsPanel({ tenantId, tenantName, canEdit = true }: {
  tenantId: string
  tenantName: string
  /** `customers:write` — a grant hands out spendable credit. */
  canEdit?: boolean
}) {
  const { t } = useTranslation('billing')
  const [plans, setPlans] = useState<PlanView[]>([])
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [refresh, setRefresh] = useState(0)

  useEffect(() => {
    billingApi.listPlans()
      .then((r) => { setPlans(r.plans); setEnabled(r.billingEnabled) })
      .catch(() => setEnabled(false))
  }, [])

  const bannerOff = enabled === false

  return (
    <div>
      <div className="themed-text text-sm mb-2 flex items-center">
        {t('promo.tenant.title', { defaultValue: 'Promotions' })}
        <InfoHint text={t('promo.tenant.desc', {
          defaultValue:
            'Time-limited credit or raised limits for this tenant only. A promo never reaches the payment provider — it is drawn before their included allowance.',
        })} />
      </div>

      {bannerOff && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 mb-3">
          <p className="text-amber-300 text-xs font-medium">{t('promo.billingOff')}</p>
        </div>
      )}

      {/* The LIST first, then the form: on a customer's page the question is
          almost always "what do they already have?", and the answer used to
          require typing their id into a box under three create forms. */}
      <GrantList
        tenantId={tenantId}
        refreshKey={refresh}
        canEdit={canEdit}
        onRevoked={() => setRefresh((n) => n + 1)}
      />

      {canEdit && (
        <div className="mt-3">
          {/* F-28 — folded. Granting a promo is occasional; reading what a
              tenant already holds is why the tab gets opened. The list above
              is that answer and now starts at the top of the block instead of
              below a six-field form and a repeating effect builder. */}
          <ActionDisclosure
            label={t('promo.form.openGrant', { defaultValue: 'Give this tenant a promotion' })}
            openLabel={t('common:cancel', { defaultValue: 'Cancel' })}
            hint={t('promo.form.openGrantHint', {
              defaultValue: 'Credit, extra storage, more concurrent runs, or a plan override — for a fixed window.',
            })}
          >
            <GrantForm
              plans={plans}
              busy={bannerOff}
              fixedTenant={{ id: tenantId, name: tenantName }}
              onCreated={() => setRefresh((n) => n + 1)}
            />
          </ActionDisclosure>
        </div>
      )}
    </div>
  )
}
